import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Filter,
  GitCompareArrows,
  History,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  TestTube2,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import type { CalcSnapshotTestCaseFacets } from '@shared/calculations/snapshot-testing';
import AdvisorDashboardShell from '@/components/AdvisorDashboardShell';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import {
  type WizardCalcSnapshotRun,
  type WizardCalcSnapshotRunResult,
  type WizardCalcSnapshotRunResultSummary,
  type WizardCalcSnapshotTestCase,
} from '@/features/wizard-calc-snapshots/api/wizard-calc-snapshots-api';
import {
  useRunWizardCalcSnapshots,
  useWizardCalcSnapshotRuns,
  useWizardCalcSnapshotTestCases,
} from '@/features/wizard-calc-snapshots/hooks/use-wizard-calc-snapshots';
import {
  CalcSnapshotDetailSheet,
  ScenarioBadges,
} from '@/features/wizard-calc-snapshots/components/CalcSnapshotDetailSheet';
import { CalcSnapshotPinDialog } from '@/features/wizard-calc-snapshots/components/CalcSnapshotPinDialog';
import {
  CalcSnapshotRunSheet,
  RunStatusBadge,
} from '@/features/wizard-calc-snapshots/components/CalcSnapshotRunSheet';
import {
  CALC_SNAPSHOT_RUN_ERROR_MESSAGE,
  ResultBadge,
  formatCalcSnapshotGitRevision,
  formatSnapshotDateTime,
  getCalcSnapshotErrorMessage,
} from '@/features/wizard-calc-snapshots/components/CalcSnapshotUi';

const DEFAULT_TAB = 'test-cases';
const VALID_TABS = new Set([DEFAULT_TAB, 'runs', 'coverage']);

interface LatestTestCaseResult {
  run: WizardCalcSnapshotRun;
  result: WizardCalcSnapshotRunResultSummary;
}

export function WizardCalcSnapshotsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') ?? DEFAULT_TAB;
  const selectedTab = VALID_TABS.has(requestedTab) ? requestedTab : DEFAULT_TAB;
  const [pinOpen, setPinOpen] = useState(false);
  const [selectedTestCaseId, setSelectedTestCaseId] = useState<string | null>(null);
  const [selectedRunResult, setSelectedRunResult] = useState<WizardCalcSnapshotRunResult | null>(
    null,
  );
  const [selectedHistoricalRunResultId, setSelectedHistoricalRunResultId] = useState<string | null>(
    null,
  );
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [jointFilter, setJointFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [search, setSearch] = useState('');
  const testCasesQuery = useWizardCalcSnapshotTestCases();
  const runsQuery = useWizardCalcSnapshotRuns();
  const runMutation = useRunWizardCalcSnapshots();

  const testCases = testCasesQuery.data ?? [];
  const runs = runsQuery.data ?? [];
  const selectedRun = runs.find(run => run.runId === selectedRunId) ?? null;
  const latestRun = runs[0] ?? null;
  const strategies = useMemo(
    () => [...new Set(testCases.map(testCase => testCase.strategyId))].sort(),
    [testCases],
  );
  const tags = useMemo(
    () => [...new Set(testCases.flatMap(testCase => testCase.tags))].sort(),
    [testCases],
  );
  const latestByTestCase = useMemo(() => {
    const resultMap = new Map<string, LatestTestCaseResult>();
    const revisionByTestCaseId = new Map(
      testCases.map(testCase => [testCase.testCaseId, testCase.revision]),
    );
    runs.forEach(run => {
      run.results.forEach(result => {
        if (
          result.testCaseRevision === revisionByTestCaseId.get(result.testCaseId) &&
          !resultMap.has(result.testCaseId)
        ) {
          resultMap.set(result.testCaseId, { run, result });
        }
      });
    });
    return resultMap;
  }, [runs, testCases]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return testCases.filter(testCase => {
      if (strategyFilter !== 'all' && testCase.strategyId !== strategyFilter) return false;
      if (jointFilter !== 'all' && String(testCase.facets.isJoint) !== jointFilter) return false;
      if (tagFilter !== 'all' && !testCase.tags.includes(tagFilter)) return false;
      const haystack =
        `${testCase.name} ${testCase.description} ${testCase.sourceClientId} ${testCase.strategyId} ${testCase.tags.join(' ')}`.toLowerCase();
      return !query || haystack.includes(query);
    });
  }, [jointFilter, search, strategyFilter, tagFilter, testCases]);
  const coverageCount = COVERAGE_CHECKS.filter(check =>
    testCases.some(testCase => check.matches(testCase.facets)),
  ).length;

  const onTabChange = (tab: string) => {
    setSearchParams(
      current => {
        const next = new URLSearchParams(current);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  const openTestCase = (testCaseId: string, result: WizardCalcSnapshotRunResult | null = null) => {
    setSelectedHistoricalRunResultId(null);
    setSelectedRunResult(result);
    setSelectedTestCaseId(testCaseId);
  };

  const openHistoricalRunResult = (testCaseId: string, runResultId: string) => {
    setSelectedRunId(null);
    setSelectedRunResult(null);
    setSelectedHistoricalRunResultId(runResultId);
    setSelectedTestCaseId(testCaseId);
  };

  const runSingle = (testCaseId: string) => {
    runMutation.mutate(
      { scope: 'single', testCaseId },
      {
        onSuccess: response =>
          openTestCase(
            testCaseId,
            response.results.find(result => result.testCaseId === testCaseId) ?? null,
          ),
      },
    );
  };

  const runScope = (scope: 'all' | 'tag', tag?: string) => {
    runMutation.mutate(scope === 'tag' ? { scope, tag } : { scope }, {
      onSuccess: response => setSelectedRunId(response.run.runId),
    });
  };

  return (
    <div className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Calculation Snapshot Tests"
        description="Pin policy calculations, run deterministic regression checks, and review field-level differences."
        actions={
          <>
            <Button
              variant="outline"
              disabled={runMutation.isPending || testCases.length === 0}
              onClick={() => runScope('all')}
            >
              {runMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Play className="size-4" />
              )}
              Run all
            </Button>
            <Button onClick={() => setPinOpen(true)}>
              <Plus className="size-4" />
              Pin test case
            </Button>
          </>
        }
      />

      <SnapshotSummary
        testCaseCount={testCases.length}
        coverageCount={coverageCount}
        latestRun={latestRun}
      />

      {runMutation.error && (
        <Alert
          variant="destructive"
          className="mt-4"
        >
          <AlertTriangle />
          <AlertTitle>Unable to run calculation tests</AlertTitle>
          <AlertDescription>{CALC_SNAPSHOT_RUN_ERROR_MESSAGE}</AlertDescription>
        </Alert>
      )}

      <Tabs
        value={selectedTab}
        onValueChange={onTabChange}
        className="mt-6"
      >
        <TabsList className="bg-primary-100 mb-6 h-12 max-w-full justify-start overflow-x-auto p-1">
          <TabsTrigger
            value="test-cases"
            className="h-full px-5 text-base font-semibold"
          >
            Test Cases
            <Badge
              variant="secondary"
              className="ml-2"
            >
              {testCases.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="runs"
            className="h-full px-5 text-base font-semibold"
          >
            Run History
          </TabsTrigger>
          <TabsTrigger
            value="coverage"
            className="h-full px-5 text-base font-semibold"
          >
            Coverage
          </TabsTrigger>
        </TabsList>

        <TabsContent value="test-cases">
          <TestCaseCatalog
            testCases={filtered}
            totalCount={testCases.length}
            latestByTestCase={latestByTestCase}
            isLoading={testCasesQuery.isLoading}
            error={testCasesQuery.error}
            isFetching={testCasesQuery.isFetching}
            isRunning={runMutation.isPending}
            search={search}
            onSearchChange={setSearch}
            strategyFilter={strategyFilter}
            onStrategyFilterChange={setStrategyFilter}
            strategies={strategies}
            jointFilter={jointFilter}
            onJointFilterChange={setJointFilter}
            tagFilter={tagFilter}
            onTagFilterChange={setTagFilter}
            tags={tags}
            onRefresh={() => testCasesQuery.refetch()}
            onPin={() => setPinOpen(true)}
            onRunTag={() => {
              if (tagFilter !== 'all') runScope('tag', tagFilter);
            }}
            onRunSingle={runSingle}
            onOpenTestCase={testCaseId => openTestCase(testCaseId)}
          />
        </TabsContent>

        <TabsContent value="runs">
          <RunHistory
            runs={runs}
            isLoading={runsQuery.isLoading}
            error={runsQuery.error}
            isFetching={runsQuery.isFetching}
            onRefresh={() => runsQuery.refetch()}
            onOpenRun={setSelectedRunId}
          />
        </TabsContent>

        <TabsContent value="coverage">
          <CoveragePanel testCases={testCases} />
        </TabsContent>
      </Tabs>

      <CalcSnapshotPinDialog
        open={pinOpen}
        onOpenChange={setPinOpen}
        onPinned={testCaseId => openTestCase(testCaseId)}
      />
      <CalcSnapshotDetailSheet
        key={selectedTestCaseId ?? 'closed'}
        testCaseId={selectedTestCaseId}
        runResult={selectedRunResult}
        historicalRunResultId={selectedHistoricalRunResultId}
        onOpenChange={open => {
          if (!open) {
            setSelectedTestCaseId(null);
            setSelectedRunResult(null);
            setSelectedHistoricalRunResultId(null);
          }
        }}
      />
      <CalcSnapshotRunSheet
        run={selectedRun}
        testCases={testCases}
        onReviewResult={openHistoricalRunResult}
        onOpenChange={open => !open && setSelectedRunId(null)}
      />
    </div>
  );
}

export function WizardCalcSnapshotsDashboardPage() {
  return <AdvisorDashboardShell content={<WizardCalcSnapshotsPage />} />;
}

function SnapshotSummary({
  testCaseCount,
  coverageCount,
  latestRun,
}: {
  testCaseCount: number;
  coverageCount: number;
  latestRun: WizardCalcSnapshotRun | null;
}) {
  const passRate = latestRun?.aggregate.testCaseCount
    ? Math.round((latestRun.aggregate.passedCount / latestRun.aggregate.testCaseCount) * 100)
    : null;
  const warningTestCaseCount =
    latestRun?.results.filter(result => result.warningCount > 0).length ?? 0;

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryCard
        icon={<TestTube2 />}
        label="Active test cases"
        value={String(testCaseCount)}
        detail="Frozen policy scenarios"
      />
      <SummaryCard
        icon={<ShieldCheck />}
        label="Scenario coverage"
        value={`${coverageCount}/${COVERAGE_CHECKS.length}`}
        detail="Core calculation variants"
      />
      <SummaryCard
        icon={<CheckCircle2 />}
        label="Latest pass rate"
        value={passRate == null ? '—' : `${passRate}%`}
        detail={
          latestRun
            ? `${latestRun.aggregate.passedCount} of ${latestRun.aggregate.testCaseCount} passed`
            : 'No runs yet'
        }
        tone={latestRun ? (latestRun.aggregate.failedCount ? 'danger' : 'success') : 'neutral'}
      />
      <SummaryCard
        icon={<Clock3 />}
        label="Latest run"
        value={latestRun ? formatRelativeDate(latestRun.createdDateTime) : 'Never'}
        detail={
          latestRun
            ? `${latestRun.aggregate.failedCount} failed · ${warningTestCaseCount} with warnings`
            : 'Run the suite to establish a baseline'
        }
      />
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  tone?: 'neutral' | 'success' | 'danger';
}) {
  const valueClass =
    tone === 'danger' ? 'text-red-700' : tone === 'success' ? 'text-green-700' : 'text-gray-950';
  return (
    <Card className="gap-0 py-0 shadow-none">
      <CardContent className="flex items-start gap-4 p-5">
        <span className="bg-primary-100 text-primary-dark flex size-10 shrink-0 items-center justify-center rounded-lg [&>svg]:size-5">
          {icon}
        </span>
        <div className="min-w-0">
          <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
            {label}
          </p>
          <p className={`mt-1 text-2xl font-semibold ${valueClass}`}>{value}</p>
          <p className="text-muted-foreground mt-1 truncate text-xs">{detail}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function TestCaseCatalog({
  testCases,
  totalCount,
  latestByTestCase,
  isLoading,
  error,
  isFetching,
  isRunning,
  search,
  onSearchChange,
  strategyFilter,
  onStrategyFilterChange,
  strategies,
  jointFilter,
  onJointFilterChange,
  tagFilter,
  onTagFilterChange,
  tags,
  onRefresh,
  onPin,
  onRunTag,
  onRunSingle,
  onOpenTestCase,
}: {
  testCases: WizardCalcSnapshotTestCase[];
  totalCount: number;
  latestByTestCase: Map<string, LatestTestCaseResult>;
  isLoading: boolean;
  error: unknown;
  isFetching: boolean;
  isRunning: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  strategyFilter: string;
  onStrategyFilterChange: (value: string) => void;
  strategies: string[];
  jointFilter: string;
  onJointFilterChange: (value: string) => void;
  tagFilter: string;
  onTagFilterChange: (value: string) => void;
  tags: string[];
  onRefresh: () => void;
  onPin: () => void;
  onRunTag: () => void;
  onRunSingle: (testCaseId: string) => void;
  onOpenTestCase: (testCaseId: string) => void;
}) {
  return (
    <div className="space-y-4">
      <Card className="gap-4 py-4 shadow-none">
        <CardContent className="px-4 sm:px-5">
          <div className="grid gap-3 lg:grid-cols-[minmax(240px,1fr)_190px_170px_180px_auto]">
            <div className="relative">
              <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2" />
              <Input
                value={search}
                onChange={event => onSearchChange(event.target.value)}
                placeholder="Search test cases, clients, or tags"
                className="pl-9"
              />
            </div>
            <Select
              value={strategyFilter}
              onValueChange={onStrategyFilterChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All strategies</SelectItem>
                {strategies.map(strategy => (
                  <SelectItem
                    key={strategy}
                    value={strategy}
                  >
                    {strategy}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={jointFilter}
              onValueChange={onJointFilterChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All policies</SelectItem>
                <SelectItem value="false">Single life</SelectItem>
                <SelectItem value="true">Joint</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={tagFilter}
              onValueChange={onTagFilterChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {tags.map(tag => (
                  <SelectItem
                    key={tag}
                    value={tag}
                  >
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              {tagFilter !== 'all' && (
                <Button
                  variant="outline"
                  disabled={isRunning}
                  onClick={onRunTag}
                >
                  <Play className="size-4" />
                  Run tag
                </Button>
              )}
              <Button
                variant="outline"
                size="icon"
                aria-label="Refresh test cases"
                disabled={isFetching}
                onClick={onRefresh}
              >
                <RefreshCw className={`size-4 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          <div className="text-muted-foreground mt-3 flex items-center gap-2 text-xs">
            <Filter className="size-3.5" />
            Showing {testCases.length} of {totalCount} test cases
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-muted-foreground flex min-h-64 items-center justify-center gap-2 rounded-xl border">
          <Loader2 className="size-5 animate-spin" />
          Loading test cases…
        </div>
      ) : error && totalCount === 0 ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Unable to load calculation test cases</AlertTitle>
          <AlertDescription>{getCalcSnapshotErrorMessage(error)}</AlertDescription>
        </Alert>
      ) : testCases.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <TestTube2 />
            </EmptyMedia>
            <EmptyTitle>
              {totalCount === 0 ? 'No calculation test cases yet' : 'No matching test cases'}
            </EmptyTitle>
            <EmptyDescription>
              {totalCount === 0
                ? 'Pin the first verified policy calculation to create a repeatable regression test.'
                : 'Adjust the search or filters to see more test cases.'}
            </EmptyDescription>
          </EmptyHeader>
          {totalCount === 0 && (
            <EmptyContent>
              <Button onClick={onPin}>
                <Plus className="size-4" />
                Pin test case
              </Button>
            </EmptyContent>
          )}
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <Table className="min-w-[1040px]">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="pl-4">Test case</TableHead>
                <TableHead>Scenario</TableHead>
                <TableHead>Policy / engine</TableHead>
                <TableHead>Frozen snapshot</TableHead>
                <TableHead>Latest run</TableHead>
                <TableHead className="pr-4 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {testCases.map(testCase => {
                const latest = latestByTestCase.get(testCase.testCaseId);
                return (
                  <TableRow key={testCase.testCaseId}>
                    <TableCell className="max-w-80 whitespace-normal py-4 pl-4">
                      <button
                        type="button"
                        className="text-left"
                        onClick={() => onOpenTestCase(testCase.testCaseId)}
                      >
                        <span className="hover:text-primary block font-semibold text-gray-950">
                          {testCase.name}
                        </span>
                        <span className="text-muted-foreground mt-1 line-clamp-1 block text-xs">
                          {testCase.description || testCase.sourceClientId}
                        </span>
                      </button>
                      {testCase.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {testCase.tags.slice(0, 3).map(tag => (
                            <Badge
                              key={tag}
                              variant="secondary"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-64 whitespace-normal py-4">
                      <div className="flex flex-wrap gap-1">
                        <ScenarioBadges facets={testCase.facets} />
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <p className="font-medium text-gray-900">{testCase.policyType}</p>
                      <p className="text-muted-foreground mt-1 text-xs">{testCase.strategyId}</p>
                    </TableCell>
                    <TableCell className="py-4">
                      <p className="font-medium text-gray-900">{testCase.asOfDate}</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Revision {testCase.revision}
                      </p>
                    </TableCell>
                    <TableCell className="py-4">
                      {latest ? (
                        <div>
                          <ResultBadge result={latest.result} />
                          <p className="text-muted-foreground mt-1 text-xs">
                            {formatRelativeDate(latest.run.createdDateTime)}
                          </p>
                        </div>
                      ) : (
                        <Badge variant="secondary">Not run</Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-4 pr-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isRunning}
                          onClick={() => onRunSingle(testCase.testCaseId)}
                        >
                          <Play className="size-4" />
                          Run
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onOpenTestCase(testCase.testCaseId)}
                        >
                          Review
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function RunHistory({
  runs,
  isLoading,
  error,
  isFetching,
  onRefresh,
  onOpenRun,
}: {
  runs: WizardCalcSnapshotRun[];
  isLoading: boolean;
  error: unknown;
  isFetching: boolean;
  onRefresh: () => void;
  onOpenRun: (runId: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-950">Run history</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Review suite results and drill into expected-versus-actual calculation differences.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={isFetching}
          onClick={onRefresh}
        >
          <RefreshCw className={`size-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground flex min-h-64 items-center justify-center gap-2 rounded-xl border">
          <Loader2 className="size-5 animate-spin" />
          Loading run history…
        </div>
      ) : error && runs.length === 0 ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Unable to load run history</AlertTitle>
          <AlertDescription>{getCalcSnapshotErrorMessage(error)}</AlertDescription>
        </Alert>
      ) : runs.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <History />
            </EmptyMedia>
            <EmptyTitle>No runs yet</EmptyTitle>
            <EmptyDescription>
              Run one test case or the full suite to create the first comparison record.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="pl-4">Run</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Results</TableHead>
                <TableHead>Triggered by</TableHead>
                <TableHead className="pr-4 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map(run => (
                <TableRow key={run.runId}>
                  <TableCell className="py-4 pl-4">
                    <p className="font-medium text-gray-950">
                      {formatSnapshotDateTime(run.createdDateTime)}
                    </p>
                    <p className="text-muted-foreground mt-1 font-mono text-xs">
                      {formatCalcSnapshotGitRevision(run.gitSha)}
                    </p>
                  </TableCell>
                  <TableCell className="capitalize">{run.scope}</TableCell>
                  <TableCell>
                    <RunStatusBadge run={run} />
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-green-700">
                      {run.aggregate.passedCount} passed
                    </span>
                    <span className="text-muted-foreground"> · </span>
                    <span
                      className={
                        run.aggregate.failedCount ? 'font-medium text-red-700' : 'text-gray-700'
                      }
                    >
                      {run.aggregate.failedCount} failed
                    </span>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {run.aggregate.warningCount} warnings
                    </p>
                  </TableCell>
                  <TableCell className="max-w-56 truncate">{run.triggeredBy}</TableCell>
                  <TableCell className="pr-4 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenRun(run.runId)}
                    >
                      <GitCompareArrows className="size-4" />
                      Review run
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function CoveragePanel({ testCases }: { testCases: WizardCalcSnapshotTestCase[] }) {
  const waitingPeriods = uniqueSorted(
    testCases.flatMap(testCase => testCase.facets.waitingPeriodDays).map(String),
  );
  const careEnvironments = uniqueSorted(
    testCases
      .flatMap(testCase => testCase.facets.careEnvironmentsVisited)
      .map(value => toLabel(String(value))),
  );
  const strategies = uniqueSorted(testCases.map(testCase => testCase.strategyId));
  const policyTypes = uniqueSorted(testCases.map(testCase => String(testCase.policyType)));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-950">Scenario coverage</h2>
        <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
          These labels are derived from the frozen bundles, so the coverage view reflects what the
          calculations actually exercise.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {COVERAGE_CHECKS.map(check => {
          const matchingCases = testCases.filter(testCase => check.matches(testCase.facets));
          return (
            <Card
              key={check.label}
              className="gap-0 py-0 shadow-none"
            >
              <CardContent className="flex items-start justify-between gap-4 p-5">
                <div>
                  <p className="font-medium text-gray-950">{check.label}</p>
                  <p className="text-muted-foreground mt-1 text-sm">{check.description}</p>
                </div>
                <Badge variant={matchingCases.length ? 'success' : 'secondary'}>
                  {matchingCases.length ? `${matchingCases.length} covered` : 'Not covered'}
                </Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="gap-0 py-0 shadow-none">
        <CardContent className="grid gap-6 p-6 sm:grid-cols-2 xl:grid-cols-4">
          <CoverageDimension
            label="Waiting periods"
            values={waitingPeriods.map(value => `${value} days`)}
          />
          <CoverageDimension
            label="Care environments"
            values={careEnvironments}
          />
          <CoverageDimension
            label="Strategies"
            values={strategies}
          />
          <CoverageDimension
            label="Policy types"
            values={policyTypes}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function CoverageDimension({ label, values }: { label: string; values: string[] }) {
  return (
    <div>
      <p className="text-sm font-semibold text-gray-950">{label}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {values.length ? (
          values.map(value => (
            <Badge
              key={value}
              variant="outline"
            >
              {value}
            </Badge>
          ))
        ) : (
          <span className="text-muted-foreground text-sm">None yet</span>
        )}
      </div>
    </div>
  );
}

const COVERAGE_CHECKS: Array<{
  label: string;
  description: string;
  matches: (facets: CalcSnapshotTestCaseFacets) => boolean;
}> = [
  {
    label: 'Reimbursement policy',
    description: 'Pays eligible care expenses up to the policy limits.',
    matches: facets => facets.isReimbursement,
  },
  {
    label: 'Indemnity policy',
    description: 'Pays the configured benefit regardless of actual care cost.',
    matches: facets => facets.isIndemnity,
  },
  {
    label: 'Joint policy',
    description: 'Pins both claimants and the shared policy calculation.',
    matches: facets => facets.isJoint,
  },
  {
    label: 'Unlimited benefit',
    description: 'Exercises a policy without a finite benefit pool.',
    matches: facets => facets.isUnlimited,
  },
  {
    label: 'Extension of benefits',
    description: 'Exercises an extension-of-benefits pool or duration.',
    matches: facets => facets.hasExtensionOfBenefits,
  },
  {
    label: 'Prior policy utilization',
    description: 'Starts the projection after some benefit has already been used.',
    matches: facets => facets.hasPriorUtilization,
  },
  {
    label: 'Already on claim',
    description: 'Exercises a client who has already started receiving LTC.',
    matches: facets => facets.hasStartedLtc,
  },
  {
    label: 'ADL assistance',
    description: 'Exercises eligibility derived from one or more assisted ADLs.',
    matches: facets => facets.adlAssistanceCount > 0,
  },
  {
    label: 'Simple inflation',
    description: 'Exercises simple policy inflation protection.',
    matches: facets => facets.inflationIsSimple,
  },
  {
    label: 'Compound inflation',
    description: 'Exercises compound policy inflation protection.',
    matches: facets => !facets.inflationIsSimple && facets.inflationRate > 0,
  },
];

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort();
}

function toLabel(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/^./, first => first.toUpperCase());
}

function formatRelativeDate(value: string): string {
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}
